import { atom } from "jotai";

import { store } from "@/lib/store";

import scenesService from "@/services/scenes.service";

import { readCatalogCache, writeCatalogCache } from "@/lib/goScenesCache";

import type { MobileSceneTab } from "@/lib/scene.helpers";

import type { SceneTemplate } from "@/lib/scene.types";

import fallbackCatalog from "@/data/sceneCatalog.fallback.json";

/**
 * Catálogo de escenas de Eriol Go (Fuente / Coming Soon / Cierre).
 *
 * ORIGEN DE VERDAD = la DB del backend (`scene_templates`, surface=mobile), vía
 * `GET /go/scenes/catalog`. Este atom es una proyección en memoria: se hidrata
 * primero desde la cache IndexedDB (o del fallback bundleado si no hay cache) y
 * luego se reconcilia con la red. Antes el catálogo era un array hardcodeado en
 * `data/mobileScenes.ts` (ese archivo quedó solo como generador del seed).
 */

const FALLBACK = fallbackCatalog as unknown as SceneTemplate[];

/**
 * Arranca con el fallback bundleado (snapshot del catálogo) para que el consumo
 * sea SÍNCRONO desde el primer render (Live/Onboarding lo usan en initializers).
 * `loadSceneCatalog` lo reemplaza por la cache IndexedDB y luego por la red.
 */
export const sceneCatalogAtom = atom<SceneTemplate[]>(FALLBACK);

export type SceneCatalogStatus = "idle" | "loading" | "ready" | "error";

export const sceneCatalogStatusAtom = atom<SceneCatalogStatus>("idle");

/** Catálogo agrupado por tab (kind). */
export const sceneCatalogByTabAtom = atom((get) => {
  const cat = get(sceneCatalogAtom);

  const byTab: Record<MobileSceneTab, SceneTemplate[]> = {
    sources: [],
    coming_soon: [],
    ending: [],
  };

  for (const s of cat) {
    const tab = s.kind as MobileSceneTab;

    if (tab === "sources" || tab === "coming_soon" || tab === "ending") {
      byTab[tab].push(s);
    }
  }

  return byTab;
});

/**
 * Carga el catálogo:
 *   1. Hidrata el atom desde IndexedDB (paint inmediato) o el fallback bundle.
 *   2. `GET /go/scenes/catalog` para reconciliar contra la DB (origen de verdad).
 *   3. Reescribe la cache IndexedDB con lo reconciliado.
 *
 * Idempotente. Escribe directo en el store jotai global (corre fuera de React).
 */
let inFlight: Promise<void> | null = null;

export async function loadSceneCatalog(): Promise<void> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    // 1. Cache IndexedDB (más fresca que el fallback bundleado inicial).
    const cached = await readCatalogCache();

    if (cached && cached.length > 0) {
      store.set(sceneCatalogAtom, cached);
    }

    store.set(sceneCatalogStatusAtom, "loading");

    // 2. Reconciliar contra la DB.
    try {
      const remote = await scenesService.getScenesCatalog();

      if (Array.isArray(remote) && remote.length > 0) {
        store.set(sceneCatalogAtom, remote);

        // 3. Refrescar la cache.
        void writeCatalogCache(remote);
      }

      store.set(sceneCatalogStatusAtom, "ready");
    } catch {
      // Red caída / backend abajo: nos quedamos con cache/fallback.
      store.set(sceneCatalogStatusAtom, "error");
    }
  })();

  try {
    await inFlight;
  } finally {
    inFlight = null;
  }
}
